import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Shield, Lock, Eye, Database } from 'lucide-react';
import { Link } from 'react-router-dom';
import AppLayout from '../layout/AppLayout';

const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation();

  return (
    <AppLayout>
      <div className="flex-1 overflow-auto bg-gradient-to-br from-primary-500 to-secondary-500">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Shield className="w-12 h-12 text-primary-500" />
                <h1 className="text-4xl font-bold text-gray-900">
                  {t('privacy.title')}
                </h1>
              </div>
              <p className="text-gray-600 text-lg">
                {t('privacy.lastUpdated')}: {new Date().toLocaleDateString()}
              </p>
            </div>

            <div className="prose prose-lg max-w-none text-gray-800">
              {/* Introduction */}
              <section className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <Eye className="w-6 h-6 text-primary-500" />
                  <h2 className="text-2xl font-semibold text-gray-900">
                    {t('privacy.sections.introduction.title')}
                  </h2>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  {t('privacy.sections.introduction.content')}
                </p>
              </section>

              {/* Information We Collect */}
              <section className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <Database className="w-6 h-6 text-primary-500" />
                  <h2 className="text-2xl font-semibold text-gray-900">
                    {t('privacy.sections.dataCollection.title')}
                  </h2>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  {t('privacy.sections.dataCollection.content')}
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>{t('privacy.sections.dataCollection.types.account')}</li>
                  <li>{t('privacy.sections.dataCollection.types.vehicle')}</li>
                  <li>{t('privacy.sections.dataCollection.types.usage')}</li>
                  <li>{t('privacy.sections.dataCollection.types.technical')}</li>
                </ul>
              </section>

              {/* How We Use Information */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  {t('privacy.sections.dataUsage.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  {t('privacy.sections.dataUsage.content')}
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>{t('privacy.sections.dataUsage.purposes.service')}</li>
                  <li>{t('privacy.sections.dataUsage.purposes.improvement')}</li>
                  <li>{t('privacy.sections.dataUsage.purposes.support')}</li>
                  <li>{t('privacy.sections.dataUsage.purposes.communication')}</li>
                </ul>
              </section>

              {/* Data Protection */}
              <section className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <Lock className="w-6 h-6 text-primary-500" />
                  <h2 className="text-2xl font-semibold text-gray-900">
                    {t('privacy.sections.protection.title')}
                  </h2>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg mb-4">
                  <p className="text-green-800">
                    {t('privacy.sections.protection.security')}
                  </p>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {t('privacy.sections.protection.content')}
                </p>
              </section>

              {/* Your Rights */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  {t('privacy.sections.rights.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  {t('privacy.sections.rights.content')}
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>{t('privacy.sections.rights.list.access')}</li>
                  <li>{t('privacy.sections.rights.list.correct')}</li>
                  <li>{t('privacy.sections.rights.list.delete')}</li>
                  <li>{t('privacy.sections.rights.list.portability')}</li>
                </ul>
              </section>

              {/* Cookies */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  {t('privacy.sections.cookies.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  {t('privacy.sections.cookies.content')}
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                  <p className="text-blue-800">
                    {t('privacy.sections.cookies.management')}
                  </p>
                </div>
              </section>

              {/* Contact Information */}
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  {t('privacy.sections.contact.title')}
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {t('privacy.sections.contact.content')}
                </p>
              </section>

              {/* Additional Links */}
              <section className="pt-8 border-t border-gray-200">
                <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
                  <Link
                    to="/terms"
                    className="hover:text-primary-500 underline transition-colors"
                  >
                    {t('terms.title')}
                  </Link>
                  <span>•</span>
                  <Link
                    to="/cookie-settings"
                    className="hover:text-primary-500 underline transition-colors"
                  >
                    {t('cookieSettings.title')}
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default PrivacyPolicy;
